/**
 * Going somewhere from code, rather than from a link.
 *
 * The link components decide between a `<router-link>` and an `<a href>` through
 * RouterCheckAvailabilityMixin. This is the same decision for the other half of the app's
 * navigation - the redirect after "create", the jump to a comment, the error page bounce -
 * which was written when there was a router per page and so had no choice but to hand the
 * url to the browser.
 *
 * Holds the router rather than importing it, for the reason `~/vue/createApp` does not
 * import it either: `spa/router` reaches the whole route table, and this module is imported
 * by route components and by plain services alike. `spa/mount` registers it.
 *
 * @see spa/mounted for why the mounted flag is read at call time
 */
import { isSpaMounted } from './mounted';
import { hideDimmer } from 'shared/utils/dimmer';

let router = null;

export function setRouter (instance) {
    router = instance;
}

/**
 * The path part of a url this document could route to, or null for one it could not:
 * another origin, a `mailto:`, an empty value.
 */
function routablePath (link) {
    if (!link || typeof link !== 'string') {
        return null;
    }
    let url;
    try {
        url = new URL(link, document.location.origin);
    } catch (e) {
        return null;
    }
    if (url.origin !== document.location.origin) {
        return null;
    }
    return url.pathname + url.search + url.hash;
}

/** Whether the app in this document can render `link` itself. */
export function canRouteTo (link) {
    if (!router || !isSpaMounted()) {
        return false;
    }
    const path = routablePath(link);
    if (!path) {
        return false;
    }
    const matched = router.resolve(path)?.matched;
    if (!matched?.length) {
        return false;
    }
    return matched.some(record => !record.meta.isErrorPage);
}

/**
 * Go to `link` - inside the router when this document can render it, through the server
 * when it cannot.
 *
 * A server navigation leaves the loading spinner alone on purpose: the callers that show it
 * never hid it, because the document was going away and the spinner covered the wait. A
 * router push has no wait to cover, so it takes the spinner down instead.
 *
 * Not for a navigation that has to reach the server - after a login, a logout, an account
 * deletion. `$user` is still rendered into the document, so a session change needs a new
 * one; those call sites set `document.location.href` directly and say why.
 *
 * @returns {boolean} whether the router took it
 */
export function navigateTo (link) {
    if (!canRouteTo(link)) {
        document.location.href = link;
        return false;
    }
    hideDimmer();
    router.push(link);
    return true;
}

export default navigateTo;
